import { getFileIcon } from "helper/Common/CommonHelper";
import downloadFileHandler from "helper/DownloadFile";
import React from "react";
import { useTranslation } from "react-i18next";

/**
 * A reusable component for uploading multiple files with a preview list.
 * @param {object} props - The component props.
 * @param {string} props.id - A unique ID for the file input.
 * @param {string} props.label - The main label for the uploader section.
 * @param {React.RefObject} props.fileInputRef - The ref for the hidden file input element.
 * @param {function} props.onFileChange - The function to call when files are selected.
 * @param {function} props.onFileRemove - The function to call to remove a file from the list. It receives the index and the file object.
 * @param {boolean} props.isUploading - A boolean to show/hide the loading indicator.
 * @param {Array<object>} props.uploadedFiles - An array of file objects to display in the list. Each object must have a `name` property.
 * @param {string} [props.helperText="Max file size: 20MB. Supported formats: DOC, PDF, PNG, MP4"] - The instructional text in the upload box.
 * @param {string} [props.acceptedFormats] - Optional string of accepted file formats (e.g., ".pdf,.doc,image/*").
 */
const MultipleFileUploader = ({
  id,
  label,
  fileInputRef,
  onFileChange,
  onFileRemove,
  isUploading,
  uploadedFiles,
  helperText = "Max file size: 20MB. Supported formats: DOC, PDF, PNG, MP4",
  acceptedFormats = "*",
}) => {
  const { t } = useTranslation();

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="form-group mb-3 mb-sm-4">
      <label className="d-block fs-sm fw-semibold mb-2">{t(label)}</label>
      <div
        className="upload-box border border-dashed border-2 rounded-10 p-4 p-lg-5 text-center"
        onClick={handleUploadClick}
        style={{ cursor: "pointer" }}
      >
        <div className="mb-3">
          <span
            className="material-symbols-outlined text-primary"
            style={{ fontSize: "4rem" }}
          >
            cloud_upload
          </span>
        </div>
        <h5 className="fw-bold mb-1">{t("Click to upload")}</h5>
        <p className="text-muted fs-sm mb-0">{t(helperText)}</p>
        <input
          type="file"
          id={id}
          className="d-none"
          ref={fileInputRef}
          onChange={onFileChange}
          multiple
          disabled={isUploading}
          accept={acceptedFormats}
        />
      </div>

      {isUploading && (
        <div className="d-flex align-items-center text-primary mt-3">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mb-0 fw-semibold fs-sm">
            {t("Uploading files, please wait...")}
          </p>
        </div>
      )}

      {!isUploading && uploadedFiles?.length > 0 && (
        <div className="uploaded-files-list d-flex flex-column gap-3 mt-4">
          <h6 className="fw-bold mb-0">{t("Uploaded Files")}</h6>
          {uploadedFiles.map((file, index) => (
            <div
              className="file-item d-flex align-items-center p-2 border border-gray-300 rounded-3 shadow-sm"
              key={index}
            >
              <span className="material-symbols-outlined text-primary me-3">
                {getFileIcon(file.name)}
              </span>
              <p
                className="fw-medium text-truncate me-auto mb-0"
                title={file.name}
              >
                {file.name}
              </p>
              <button
                type="button"
                className="btn btn-sm btn-icon btn-light me-2"
                title={t("Download")}
                onClick={() => downloadFileHandler(file)}
              >
                <span className="d-block material-symbols-outlined">
                  download
                </span>
              </button>
              <button
                type="button"
                className="btn btn-sm btn-icon btn-light-danger"
                title={t("Delete")}
                onClick={() => onFileRemove(index, file)}
              >
                <span className="d-block material-symbols-outlined">
                  delete
                </span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultipleFileUploader;
